/**
 * Shared API utilities for EMS Frontend
 */

import axios from 'axios';

// API base URL from environment or default
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// Create axios instance with base configuration
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token interceptor
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response error interceptor
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - could redirect to login
    if (error.response?.status === 401) {
      // Could emit an event or handle auth expiry here
      console.warn('Authentication expired');
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// Dashboard API
// ============================================================================

export interface DashboardStats {
  totalUsers: number;
  totalUsersChange: number;
  activeModules: number;
  featureFlags: number;
  featureFlagsChange: number;
  systemHealth: number;
}

export interface DashboardActivity {
  id: string;
  type: 'success' | 'warning' | 'info' | 'error';
  message: string;

  // Actor (WHO)
  userId: string | null;
  userName: string | null;
  username: string | null;
  userEmail: string | null;
  ipAddress: string | null;
  userAgent: string | null;

  // Action (WHAT)
  action: string;
  actionVerb: string;
  actionCategory: string;
  resourceType: string;
  resourceId: string | null;
  resourceName: string | null;
  displayCategory: string;

  // Status
  success: boolean;
  error: string | null;

  // Changes (DETAILS)
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changes: Array<{ field: string; oldValue: unknown; newValue: unknown }>;
  metadata: Record<string, unknown> | null;

  // Timestamp (WHEN)
  timestamp: string;
  createdAt: string;
}

export interface DashboardData {
  stats: DashboardStats;
  recentActivity: DashboardActivity[];
}

export async function fetchDashboardData(): Promise<DashboardData> {
  const response = await apiClient.get<DashboardData>('/v1/dashboard');
  return response.data;
}

// ============================================================================
// Activity API
// ============================================================================

export interface Activity {
  id: string;
  type: 'success' | 'warning' | 'info' | 'error';
  message: string;

  // Actor (WHO)
  userId: string;
  userName: string | null;
  username: string | null;
  userEmail: string | null;
  ipAddress: string | null;
  userAgent: string | null;

  // Action (WHAT)
  action: string;
  actionVerb: string;
  actionCategory: string;
  targetType: string;
  targetId: string | null;
  targetName: string | null;
  resourceType: string;
  resourceId: string | null;
  resourceName: string | null;
  displayCategory: string;

  // Status
  success: boolean;
  error: string | null;

  // Changes (DETAILS)
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  changes: Array<{ field: string; oldValue: unknown; newValue: unknown }>;
  metadata: Record<string, unknown> | null;

  // Timestamp (WHEN)
  createdAt: string;
}

export interface ActivityListResponse {
  activities: Activity[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface ActivityListParams {
  page?: number;
  pageSize?: number;
  action?: string;
  targetType?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
}

export async function fetchActivities(params: ActivityListParams = {}): Promise<ActivityListResponse> {
  const response = await apiClient.get<ActivityListResponse>('/v1/activity', { params });
  return response.data;
}

// ============================================================================
// Users API
// ============================================================================

export interface User {
  id: string;
  name: string;
  email: string;
  username: string;
  isAdmin: boolean;
  isActive: boolean;
  groups: string[];
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UsersListResponse {
  users: User[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface UsersListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'all';
  role?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export async function fetchUsers(params: UsersListParams = {}): Promise<UsersListResponse> {
  const response = await apiClient.get<UsersListResponse>('/v1/users', { params });
  return response.data;
}

export async function fetchUser(id: string): Promise<User> {
  const response = await apiClient.get<{ user: User }>(`/v1/users/${id}`);
  return response.data.user;
}

export async function createUser(data: Partial<User>): Promise<User> {
  const response = await apiClient.post<{ user: User }>('/v1/users', data);
  return response.data.user;
}

export async function updateUser(id: string, data: Partial<User>): Promise<User> {
  const response = await apiClient.patch<{ user: User }>(`/v1/users/${id}`, data);
  return response.data.user;
}

export async function deleteUser(id: string): Promise<void> {
  await apiClient.delete(`/v1/users/${id}`);
}

export interface ChangePasswordPayload {
  newPassword: string;
}

export interface ChangeOwnPasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export async function changePassword(
  userId: string,
  data: ChangePasswordPayload
): Promise<void> {
  await apiClient.post(`/v1/users/${userId}/password`, data);
}

export async function changeOwnPassword(data: ChangeOwnPasswordPayload): Promise<void> {
  await apiClient.post('/v1/users/me/password', data);
}

// ============================================================================
// Roles API
// ============================================================================

export interface Permission {
  id: string;
  key: string;
  name: string;
  resource: string;
  action: string;
  description: string | null;
  category: string;
}

export interface Role {
  id: string;
  name: string;
  displayName: string;
  description: string | null;
  isSystem: boolean;
  isDefault: boolean;
  priority: number;
  userCount?: number;
  permissionCount?: number;
  permissions?: Permission[];
  users?: { id: string; email: string; name: string; username: string }[];
  createdAt: string;
  updatedAt: string;
}

export interface RolesListResponse {
  roles: Role[];
}

export interface PermissionsListResponse {
  permissions: Permission[];
  categories: string[];
  resources: string[];
}

export async function fetchRoles(params: { includeSystem?: boolean; search?: string } = {}): Promise<RolesListResponse> {
  const response = await apiClient.get<RolesListResponse>('/v1/roles', { params });
  return response.data;
}

export async function fetchRole(id: string): Promise<Role> {
  const response = await apiClient.get<{ role: Role }>(`/v1/roles/${id}`);
  return response.data.role;
}

export async function createRole(data: {
  name: string;
  displayName?: string;
  description?: string;
  permissions?: string[];
  isDefault?: boolean;
  priority?: number;
}): Promise<Role> {
  const response = await apiClient.post<{ role: Role }>('/v1/roles', data);
  return response.data.role;
}

export async function updateRole(id: string, data: Partial<{
  name: string;
  displayName: string;
  description: string;
  permissions: string[];
  isDefault: boolean;
  priority: number;
}>): Promise<Role> {
  const response = await apiClient.patch<{ role: Role }>(`/v1/roles/${id}`, data);
  return response.data.role;
}

export async function deleteRole(id: string): Promise<void> {
  await apiClient.delete(`/v1/roles/${id}`);
}

export async function cloneRole(id: string, data?: { name?: string }): Promise<Role> {
  const response = await apiClient.post<{ role: Role }>(`/v1/roles/${id}/clone`, data || {});
  return response.data.role;
}

export async function fetchPermissions(params: { category?: string; resource?: string } = {}): Promise<PermissionsListResponse> {
  const response = await apiClient.get<PermissionsListResponse>('/v1/roles/permissions', { params });
  return response.data;
}

export async function assignUserToRole(roleId: string, userId: string): Promise<void> {
  await apiClient.post(`/v1/roles/${roleId}/users/${userId}`);
}

export async function removeUserFromRole(roleId: string, userId: string): Promise<void> {
  await apiClient.delete(`/v1/roles/${roleId}/users/${userId}`);
}

// ============================================================================
// System Health API
// ============================================================================

export interface ServiceStatus {
  name: string;
  status: 'healthy' | 'degraded' | 'down' | 'unknown';
  message: string | null;
  latency: number | null;
  lastCheck: string;
  version?: string;
  pm2Name?: string; // PM2 process name for log viewing
}

export interface SystemMetrics {
  cpu: {
    usage: number;
    cores: number;
  };
  memory: {
    used: number;
    total: number;
    usagePercent: number;
  };
  disk: {
    used: number;
    total: number;
    usagePercent: number;
  };
  uptime: number;
}

export interface SystemHealthResponse {
  status: 'healthy' | 'degraded' | 'down';
  services: ServiceStatus[];
  metrics: SystemMetrics;
  timestamp: string;
}

// Backend response types
interface BackendServiceHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  message?: string | null;
  latencyMs?: number | null;
  lastCheck?: string;
  version?: string;
  error?: string;
}

interface BackendHealthResponse {
  status: 'healthy' | 'degraded' | 'unhealthy';
  timestamp: string;
  uptime: number;
  version: string;
  environment: string;
  services: {
    apiServer: BackendServiceHealth;
    database: BackendServiceHealth;
    redis: BackendServiceHealth;
    queue: BackendServiceHealth;
    emsSm?: BackendServiceHealth;
    emsDvs: BackendServiceHealth;
    emsCvs: BackendServiceHealth;
  };
}

interface BackendMetricsResponse {
  timestamp: string;
  process: {
    uptime: number;
    pid: number;
    memoryUsage: {
      rss: number;
      heapTotal: number;
      heapUsed: number;
      external: number;
      arrayBuffers: number;
    };
    cpuUsage: {
      user: number;
      system: number;
    };
  };
  system: {
    platform: string;
    arch: string;
    nodeVersion: string;
    cpus: {
      count: number;
      model: string;
      speed: number;
    };
    cpuUsagePercent: number;
    memory: {
      total: number;
      free: number;
      used: number;
      usagePercent: number;
    };
    loadAverage: number[];
    uptime: number;
  };
  disk?: {
    total: number;
    used: number;
    free: number;
    usagePercent: number;
  };
}

export async function fetchSystemHealth(): Promise<SystemHealthResponse> {
  // Fetch both health and metrics in parallel
  const [healthResponse, metricsResponse] = await Promise.all([
    apiClient.get<BackendHealthResponse>('/v1/system/health'),
    apiClient.get<BackendMetricsResponse>('/v1/system/metrics'),
  ]);

  const health = healthResponse.data;
  const metrics = metricsResponse.data;

  // Helper to safely extract service status with defaults
  const getServiceStatus = (
    service: BackendServiceHealth | undefined,
    name: string,
    pm2Name?: string
  ): ServiceStatus => ({
    name,
    status: service?.status === 'unhealthy' ? 'down' : (service?.status || 'healthy'),
    message: service?.message || null,
    latency: service?.latencyMs ?? null,
    lastCheck: service?.lastCheck || health.timestamp,
    version: service?.version,
    pm2Name,
  });

  // Transform services to array format — mode-aware (SMS only runs on master)
  const services: ServiceStatus[] = [
    getServiceStatus(health.services.apiServer, 'EMS-API Service', 'ems-api'),
    getServiceStatus(health.services.database, 'PostgreSQL Database'),
    getServiceStatus(health.services.redis, 'Redis Cache'),
    getServiceStatus(health.services.queue, 'BullMQ Queue'),
    getServiceStatus(health.services.emsDvs, 'EMS-Document Validation Service', 'ems-dvs'),
    getServiceStatus(health.services.emsCvs, 'EMS-Credential Vault Service', 'ems-cvs'),
  ];

  // SMS only present on master — include conditionally
  if (health.services.emsSm) {
    services.push(getServiceStatus(health.services.emsSm, 'EMS-Licensing Management Service', 'ems-sm'));
  }

  // Map status from backend to frontend format
  const status: 'healthy' | 'degraded' | 'down' =
    health.status === 'unhealthy' ? 'down' : health.status;

  return {
    status,
    services,
    metrics: {
      cpu: {
        usage: metrics.system.cpuUsagePercent,
        cores: metrics.system.cpus.count,
      },
      memory: {
        used: metrics.system.memory.used,
        total: metrics.system.memory.total,
        usagePercent: metrics.system.memory.usagePercent,
      },
      disk: metrics.disk
        ? {
            used: metrics.disk.used,
            total: metrics.disk.total,
            usagePercent: metrics.disk.usagePercent,
          }
        : {
            used: 0,
            total: 0,
            usagePercent: 0,
          },
      uptime: metrics.system.uptime,
    },
    timestamp: health.timestamp,
  };
}

// ============================================================================
// System Logs API
// ============================================================================

export interface SystemLog {
  level: 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace';
  time: string;
  msg: string;
  source: 'application' | 'audit';
  // Application log fields
  module?: string;
  pid?: number;
  workerId?: string;
  // Audit log fields
  action?: string;
  resource?: string;
  resourceId?: string | null;
  userId?: string | null;
  userName?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  success?: boolean;
  error?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface SystemLogsListParams {
  source?: 'application' | 'audit' | 'all';
  level?: 'fatal' | 'error' | 'warn' | 'info' | 'debug' | 'trace';
  module?: string;
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

export interface SystemLogsListResponse {
  logs: SystemLog[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  sources: {
    application: number;
    audit: number;
  };
}

export interface SystemLogsStats {
  byLevel: Record<string, number>;
  bySource: {
    application: number;
    audit: number;
  };
  recent: {
    lastHour: number;
    last24Hours: number;
  };
}

export async function fetchSystemLogs(params: SystemLogsListParams = {}): Promise<SystemLogsListResponse> {
  const response = await apiClient.get<SystemLogsListResponse>('/v1/system/logs', { params });
  return response.data;
}

export async function fetchSystemLogsStats(): Promise<SystemLogsStats> {
  const response = await apiClient.get<SystemLogsStats>('/v1/system/logs/stats');
  return response.data;
}

// ============================================================================
// PM2 Process Logs API
// ============================================================================

export interface PM2LogsResponse {
  processName: string;
  lines: number;
  logs: string[];
  timestamp: string;
}

export async function fetchPM2Logs(processName: string, lines: number = 300): Promise<PM2LogsResponse> {
  const response = await apiClient.get<PM2LogsResponse>(`/v1/system/logs/${processName}`, {
    params: { lines },
  });
  return response.data;
}

// ============================================================================
// Unified PM2 Logs API
// ============================================================================

export interface UnifiedLogEntry {
  service: string;
  level: string;
  time: string;
  msg: string;
  raw: string;
  metadata?: Record<string, unknown>;
}

export interface UnifiedPM2LogsResponse {
  logs: UnifiedLogEntry[];
  services: string[];
  timestamp: string;
}

export interface UnifiedPM2LogsParams {
  services?: string[];
  lines?: number;
  level?: string;
}

export async function fetchUnifiedPM2Logs(params: UnifiedPM2LogsParams = {}): Promise<UnifiedPM2LogsResponse> {
  const response = await apiClient.get<UnifiedPM2LogsResponse>('/v1/system/pm2-logs', {
    params: {
      services: params.services?.join(','),
      lines: params.lines,
      level: params.level,
    },
  });
  return response.data;
}

// Available PM2 services for the unified log viewer (master mode)
export const PM2_SERVICES = [
  { id: 'ems-api', name: 'EMS-API', description: 'Main API Server' },
  { id: 'ems-cvs', name: 'EMS-CVS', description: 'Credential Vault Service' },
  { id: 'ems-dvs', name: 'EMS-DVS', description: 'Document Validation Service' },
  { id: 'ems-sm', name: 'EMS-SM', description: 'Licensing Management Service' },
] as const;

// Available Docker services for the unified log viewer (instance mode)
export const DOCKER_SERVICES = [
  { id: 'ems-api', name: 'EMS-API', description: 'Main API Server' },
  { id: 'ems-cvs', name: 'EMS-CVS', description: 'Credential Vault Service' },
  { id: 'ems-dvs', name: 'EMS-DVS', description: 'Document Validation Service' },
  { id: 'ems-core', name: 'EMS-Core', description: 'Frontend Dashboard' },
] as const;

// Fetch Docker container logs (instance mode)
export async function fetchDockerLogs(params: {
  containers?: string[];
  lines?: number;
  level?: string;
} = {}): Promise<UnifiedPM2LogsResponse> {
  const response = await apiClient.get<UnifiedPM2LogsResponse>('/v1/system/docker-logs', {
    params: {
      containers: params.containers?.join(','),
      lines: params.lines,
      level: params.level,
    },
  });
  return response.data;
}

// ============================================================================
// Analytics API
// ============================================================================

export interface AnalyticsDashboard {
  overview: {
    users: { total: number; active: number; inactive: number };
    assets: { total: number };
    connections: { total: number };
    modules: { active: number };
  };
  activity: {
    last24h: number;
    last7dLogins: number;
  };
  discovery: {
    completed: number;
    failed: number;
    running: number;
    pending: number;
    total: number;
  };
  distribution: {
    byModule: Array<{ module: string; count: number }>;
    byType: Array<{ type: string; count: number }>;
  };
  timeline: Array<{ date: string; count: number }>;
}

export interface AnalyticsReport {
  id: string;
  name: string;
  description: string;
  category: string;
}

export async function fetchAnalyticsDashboard(): Promise<AnalyticsDashboard> {
  const response = await apiClient.get<AnalyticsDashboard>('/v1/analytics/dashboard');
  return response.data;
}

export async function fetchAnalyticsReports(): Promise<{ reports: AnalyticsReport[] }> {
  const response = await apiClient.get<{ reports: AnalyticsReport[] }>('/v1/analytics/reports');
  return response.data;
}

// ============================================================================
// User Profile API
// ============================================================================

export interface UserProfile {
  id: string;
  email: string;
  username: string;
  name: string;
  isAdmin: boolean;
  isActive: boolean;
  groups: string[];
  roles: Array<{
    id: string;
    name: string;
    displayName: string;
    permissions: string[];
  }>;
  permissions: string[];
  lastLoginAt: string | null;
  createdAt: string;
}

export interface UpdateProfileRequest {
  name?: string;
  currentPassword?: string;
  newPassword?: string;
}

export async function fetchUserProfile(): Promise<UserProfile> {
  const response = await apiClient.get<{ user: UserProfile }>('/v1/auth/me');
  return response.data.user;
}

export async function updateUserProfile(data: UpdateProfileRequest): Promise<{ user: UserProfile; message?: string }> {
  const response = await apiClient.patch<{ user: UserProfile; message?: string }>('/v1/auth/profile', data);
  return response.data;
}

// ============================================================================
// API Keys API
// ============================================================================

export interface ApiKey {
  id: string;
  name: string;
  description: string | null;
  keyPrefix: string;
  keySuffix: string;
  scopes: string[];
  metadata?: Record<string, unknown>;
  enabled: boolean;
  expiresAt: string | null;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  totalRequests: number;
  lastRequestUri: string | null;
  createdBy: string;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiKeyWithFullKey extends ApiKey {
  key: string; // Only present when creating or regenerating
}

export interface ApiKeysListResponse {
  apiKeys: ApiKey[];
}

export interface CreateApiKeyRequest {
  name: string;
  description?: string;
  scopes?: string[];
  expiresAt?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateApiKeyRequest {
  name?: string;
  description?: string;
  scopes?: string[];
  expiresAt?: string | null;
  enabled?: boolean;
  metadata?: Record<string, unknown>;
}

export async function fetchApiKeys(): Promise<ApiKeysListResponse> {
  const response = await apiClient.get<ApiKeysListResponse>('/v1/integrations/api-keys');
  return response.data;
}

export async function fetchApiKey(id: string): Promise<ApiKey> {
  const response = await apiClient.get<{ apiKey: ApiKey }>(`/v1/integrations/api-keys/${id}`);
  return response.data.apiKey;
}

export async function createApiKey(data: CreateApiKeyRequest): Promise<{ apiKey: ApiKeyWithFullKey; warning: string }> {
  const response = await apiClient.post<{ apiKey: ApiKeyWithFullKey; warning: string }>('/v1/integrations/api-keys', data);
  return response.data;
}

export async function updateApiKey(id: string, data: UpdateApiKeyRequest): Promise<ApiKey> {
  const response = await apiClient.patch<{ apiKey: ApiKey }>(`/v1/integrations/api-keys/${id}`, data);
  return response.data.apiKey;
}

export async function deleteApiKey(id: string): Promise<void> {
  await apiClient.delete(`/v1/integrations/api-keys/${id}`);
}

export async function regenerateApiKey(id: string): Promise<{ apiKey: ApiKeyWithFullKey; warning: string }> {
  const response = await apiClient.post<{ apiKey: ApiKeyWithFullKey; warning: string }>(`/v1/integrations/api-keys/${id}/regenerate`);
  return response.data;
}

// ============================================================================
// Feature Flags API
// ============================================================================

export interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string | null;
  type: 'boolean' | 'percentage' | 'user_list';
  enabled: boolean;
  value: boolean | number | string[] | null;
  environment: string;
  createdAt: string;
  updatedAt: string;
}

export interface FeatureFlagsListResponse {
  featureFlags: FeatureFlag[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export async function fetchFeatureFlags(params: {
  page?: number;
  pageSize?: number;
  environment?: string;
  type?: string;
  enabled?: boolean;
  search?: string;
} = {}): Promise<FeatureFlagsListResponse> {
  const response = await apiClient.get<FeatureFlagsListResponse>('/v1/feature-flags', { params });
  return response.data;
}

export async function fetchFeatureFlag(id: string): Promise<FeatureFlag> {
  const response = await apiClient.get<{ featureFlag: FeatureFlag }>(`/v1/feature-flags/${id}`);
  return response.data.featureFlag;
}

export async function createFeatureFlag(data: {
  key: string;
  name: string;
  description?: string;
  type: 'boolean' | 'percentage' | 'user_list';
  enabled?: boolean;
  value?: boolean | number | string[] | null;
  environment?: string;
}): Promise<FeatureFlag> {
  const response = await apiClient.post<{ featureFlag: FeatureFlag }>('/v1/feature-flags', data);
  return response.data.featureFlag;
}

export async function updateFeatureFlag(id: string, data: Partial<{
  name: string;
  description: string;
  enabled: boolean;
  value: boolean | number | string[] | null;
}>): Promise<FeatureFlag> {
  const response = await apiClient.patch<{ featureFlag: FeatureFlag }>(`/v1/feature-flags/${id}`, data);
  return response.data.featureFlag;
}

export async function toggleFeatureFlag(id: string): Promise<FeatureFlag> {
  const response = await apiClient.post<{ featureFlag: FeatureFlag }>(`/v1/feature-flags/${id}/toggle`);
  return response.data.featureFlag;
}

export async function deleteFeatureFlag(id: string): Promise<void> {
  await apiClient.delete(`/v1/feature-flags/${id}`);
}

// ============================================================================
// Modules API (for sidebar navigation)
// ============================================================================

export interface EnabledModule {
  id: string;
  name: string;
  category: string;
}

export interface EnabledModulesResponse {
  modules: EnabledModule[];
}

export async function fetchEnabledModules(): Promise<EnabledModulesResponse> {
  const response = await apiClient.get<EnabledModulesResponse>('/v1/modules/enabled/nav');
  return response.data;
}

// ============================================================================
// Authentication API
// ============================================================================

export interface LoginCredentials {
  usernameOrEmail: string;
  password: string;
}

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  name: string;
  isAdmin: boolean;
  groups: string[];
  roles: { id: string; name: string; displayName: string }[];
  permissions: string[];
  lastLoginAt: string | null;
}

export interface LoginResponse {
  token: string;
  user: AuthUser;
}

export interface CurrentUserResponse {
  user: AuthUser & {
    isActive: boolean;
    createdAt: string;
  };
}

export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/v1/auth/login', credentials);
  return response.data;
}

export async function getCurrentUser(): Promise<CurrentUserResponse> {
  const response = await apiClient.get<CurrentUserResponse>('/v1/auth/me');
  return response.data;
}

export async function logout(): Promise<void> {
  await apiClient.post('/v1/auth/logout');
}

export async function refreshToken(): Promise<LoginResponse> {
  const response = await apiClient.post<LoginResponse>('/v1/auth/refresh');
  return response.data;
}

// ============================================================================
// Setup API (First-Time Configuration)
// ============================================================================

export interface SetupStatus {
  needsSetup: boolean;
  hasAdmin: boolean;
  requiresSecurityCode?: boolean;
  securityCodeVerified?: boolean;
}

export interface VerifySecurityCodeData {
  code: string;
}

export interface VerifySecurityCodeResponse {
  valid: boolean;
  message: string;
}

export interface GeneratedPassword {
  password: string;
  strength: 'weak' | 'fair' | 'good' | 'strong';
  score: number;
}

export interface CreateAdminData {
  name: string;
  email: string;
  username: string;
  password: string;
  vaultPassphrase?: string;
}

export interface CreateAdminResponse {
  success: boolean;
  message: string;
  vaultInitialized?: boolean;
  user: {
    id: string;
    email: string;
    username: string;
    name: string;
    isAdmin: boolean;
    roles: { id: string; name: string; displayName: string }[];
  };
}

export async function getSetupStatus(): Promise<SetupStatus> {
  const response = await apiClient.get<SetupStatus>('/v1/setup/status');
  return response.data;
}

export async function verifySecurityCode(data: VerifySecurityCodeData): Promise<VerifySecurityCodeResponse> {
  const response = await apiClient.post<VerifySecurityCodeResponse>('/v1/setup/verify-code', data);
  return response.data;
}

export async function generatePassword(): Promise<GeneratedPassword> {
  const response = await apiClient.get<GeneratedPassword>('/v1/setup/generate-password');
  return response.data;
}

export async function createInitialAdmin(data: CreateAdminData): Promise<CreateAdminResponse> {
  const response = await apiClient.post<CreateAdminResponse>('/v1/setup/admin', data);
  return response.data;
}

// Setup Mode & Onboarding (Phase 2 wizard expansion)

export interface SetupModeInfo {
  mode: 'master' | 'instance';
  version: string;
  masterUrl?: string;
  features: {
    licenseActivation: boolean;
    masterPing: boolean;
    databaseTest: boolean;
  };
}

export interface DatabaseTestResult {
  connected: boolean;
  latencyMs?: number;
  version?: string;
  message: string;
  error?: string;
}

export interface MasterPingResult {
  reachable: boolean;
  masterUrl?: string;
  masterVersion?: string;
  latencyMs?: number;
  message: string;
  mode?: string;
}

export interface LicenseActivateResult {
  activated: boolean;
  message: string;
  tier?: string;
  expiresAt?: string;
  mode?: string;
}

export async function getSetupMode(): Promise<SetupModeInfo> {
  const response = await apiClient.get<SetupModeInfo>('/v1/setup/mode');
  return response.data;
}

export async function testDatabaseConnection(): Promise<DatabaseTestResult> {
  const response = await apiClient.post<{ success: boolean; data: DatabaseTestResult }>('/v1/setup/database-test');
  return response.data.data;
}

export async function pingMaster(): Promise<MasterPingResult> {
  const response = await apiClient.get<{ success: boolean; data: MasterPingResult }>('/v1/setup/master-ping');
  return response.data.data;
}

export async function activateSetupLicense(licenseKey: string): Promise<LicenseActivateResult> {
  const response = await apiClient.post<{ success: boolean; data: LicenseActivateResult }>('/v1/setup/license-activate', { licenseKey });
  return response.data.data;
}

// Setup Module Selection & Credential Provisioning

export interface EntitledModule {
  id: string;
  name: string;
  category: string;
  description: string;
  icon: string;
  color: string;
}

export interface EntitledModulesResult {
  tier: string | null;
  modules: EntitledModule[];
  message?: string;
}

export interface StoreCredentialData {
  moduleId: string;
  label: string;
  credentialType: 'username_password' | 'api_token';
  payload: Record<string, unknown>;
}

export interface StoreCredentialResult {
  success: boolean;
  credentialId: string;
}

export async function getEntitledModules(): Promise<EntitledModulesResult> {
  const response = await apiClient.get<EntitledModulesResult>('/v1/setup/entitled-modules');
  return response.data;
}

export async function enableSetupModules(moduleIds: string[]): Promise<{ enabled: string[]; count: number }> {
  const response = await apiClient.post<{ enabled: string[]; count: number }>('/v1/setup/enable-modules', { moduleIds });
  return response.data;
}

export async function storeSetupCredential(data: StoreCredentialData): Promise<StoreCredentialResult> {
  const response = await apiClient.post<StoreCredentialResult>('/v1/setup/store-credential', data);
  return response.data;
}

// ============================================================================
// User Creation API (for admin user creation dialog)
// ============================================================================

export interface CreateLocalUserData {
  email: string;
  username: string;
  name: string;
  password: string;
  isAdmin?: boolean;
  roleIds?: string[];
  groups?: string[];
}

export async function createLocalUser(data: CreateLocalUserData): Promise<User> {
  const response = await apiClient.post<{ user: User }>('/v1/users', data);
  return response.data.user;
}

/**
 * Export users to CSV file
 */
export async function exportUsers(): Promise<Blob> {
  const response = await apiClient.get('/v1/users/export', {
    responseType: 'blob',
  });
  return response.data;
}

/**
 * Import users from CSV data
 */
export interface ImportUsersResult {
  created: number;
  skipped: number;
  errors: Array<{ row: number; email: string; error: string }>;
}

export async function importUsers(csv: string): Promise<ImportUsersResult> {
  const response = await apiClient.post<ImportUsersResult>('/v1/users/import', { csv });
  return response.data;
}

// ============================================================================
// Bug Reports API
// ============================================================================

export type BugSeverity = 'low' | 'medium' | 'high' | 'critical';
export type BugStatus = 'open' | 'in_progress' | 'resolved' | 'closed' | 'wont_fix' | 'duplicate';
export type BugType = 'bug' | 'crash' | 'ui' | 'performance' | 'security' | 'other';

export interface BugReport {
  id: string;
  title: string;
  description: string;
  severity: BugSeverity;
  status: BugStatus;
  type: BugType;
  pageUrl?: string;
  componentName?: string;
  errorMessage?: string;
  errorStack?: string;
  componentStack?: string;
  sessionLogs?: Record<string, unknown>[];
  browserInfo?: Record<string, unknown>;
  consoleErrors?: Record<string, unknown>[];
  networkErrors?: Record<string, unknown>[];
  stateSnapshot?: Record<string, unknown>;
  environment?: string;
  appVersion?: string;
  userAgent?: string;
  submitterId?: string;
  submitterName?: string;
  submitterEmail?: string;
  submitter?: { id: string; displayName: string; email: string };
  assigneeId?: string;
  assignee?: { id: string; displayName: string; email: string };
  resolvedAt?: string;
  resolution?: string;
  comments?: BugComment[];
  _count?: { comments: number; attachments: number };
  createdAt: string;
  updatedAt: string;
}

export interface BugComment {
  id: string;
  bugReportId: string;
  authorId: string;
  author?: { id: string; displayName: string; email: string };
  content: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBugReportData {
  title: string;
  description: string;
  severity?: BugSeverity;
  type?: BugType;
  pageUrl?: string;
  componentName?: string;
  errorMessage?: string;
  errorStack?: string;
  componentStack?: string;
  sessionLogs?: Record<string, unknown>[];
  browserInfo?: Record<string, unknown>;
  consoleErrors?: Record<string, unknown>[];
  networkErrors?: Record<string, unknown>[];
  stateSnapshot?: Record<string, unknown>;
  environment?: string;
  appVersion?: string;
  userAgent?: string;
  submitterName?: string;
  submitterEmail?: string;
}

export interface UpdateBugReportData {
  title?: string;
  description?: string;
  severity?: BugSeverity;
  status?: BugStatus;
  type?: BugType;
  assigneeId?: string | null;
  resolution?: string;
}

export interface BugReportListParams {
  status?: BugStatus;
  severity?: BugSeverity;
  type?: BugType;
  assigneeId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface BugReportListResponse {
  bugReports: BugReport[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface BugReportStats {
  byStatus: Record<BugStatus, number>;
  bySeverity: Record<BugSeverity, number>;
  byType: Record<BugType, number>;
  recentCount: number;
  unassignedCount: number;
}

export async function createBugReport(data: CreateBugReportData): Promise<BugReport> {
  const response = await apiClient.post<{ bugReport: BugReport }>('/v1/bug-reports', data);
  return response.data.bugReport;
}

export async function submitCrashReport(data: CreateBugReportData): Promise<{ id: string; message: string }> {
  const response = await apiClient.post<{ bugReport: { id: string }; message: string }>('/v1/bug-reports/crash', data);
  return { id: response.data.bugReport.id, message: response.data.message };
}

export async function fetchBugReports(params?: BugReportListParams): Promise<BugReportListResponse> {
  const response = await apiClient.get<BugReportListResponse>('/v1/bug-reports', { params });
  return response.data;
}

export async function fetchBugReport(id: string): Promise<BugReport> {
  const response = await apiClient.get<{ bugReport: BugReport }>(`/v1/bug-reports/${id}`);
  return response.data.bugReport;
}

export async function fetchBugReportStats(): Promise<BugReportStats> {
  const response = await apiClient.get<{ stats: BugReportStats }>('/v1/bug-reports/stats');
  return response.data.stats;
}

export async function updateBugReport(id: string, data: UpdateBugReportData): Promise<BugReport> {
  const response = await apiClient.patch<{ bugReport: BugReport }>(`/v1/bug-reports/${id}`, data);
  return response.data.bugReport;
}

export async function deleteBugReport(id: string): Promise<void> {
  await apiClient.delete(`/v1/bug-reports/${id}`);
}

export async function addBugComment(bugId: string, content: string): Promise<BugComment> {
  const response = await apiClient.post<{ comment: BugComment }>(`/v1/bug-reports/${bugId}/comments`, { content });
  return response.data.comment;
}

export async function deleteBugComment(bugId: string, commentId: string): Promise<void> {
  await apiClient.delete(`/v1/bug-reports/${bugId}/comments/${commentId}`);
}

// ============================================================================
// Feature Requests API
// ============================================================================

export type FeatureStatus = 'submitted' | 'under_review' | 'planned' | 'in_progress' | 'completed' | 'declined';
export type FeaturePriority = 'low' | 'medium' | 'high' | 'critical';

export interface FeatureRequest {
  id: string;
  title: string;
  description: string;
  status: FeatureStatus;
  priority?: FeaturePriority;
  category?: string;
  useCase?: string;
  expectedBehavior?: string;
  voteCount: number;
  hasVoted?: boolean;
  submitterId: string;
  submitter?: { id: string; displayName: string; email: string };
  adminNotes?: string;
  targetRelease?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFeatureRequestData {
  title: string;
  description: string;
  category?: string;
  useCase?: string;
  expectedBehavior?: string;
}

export interface UpdateFeatureRequestData {
  title?: string;
  description?: string;
  status?: FeatureStatus;
  priority?: FeaturePriority;
  category?: string;
  useCase?: string;
  expectedBehavior?: string;
  adminNotes?: string;
  targetRelease?: string;
}

export interface FeatureRequestListParams {
  status?: FeatureStatus;
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface FeatureRequestListResponse {
  featureRequests: FeatureRequest[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export interface FeatureRequestStats {
  byStatus: Record<FeatureStatus, number>;
  byPriority: Record<FeaturePriority, number>;
  byCategory: Record<string, number>;
  topVoted: Array<{ id: string; title: string; voteCount: number; status: string }>;
  recentCount: number;
}

export async function createFeatureRequest(data: CreateFeatureRequestData): Promise<FeatureRequest> {
  const response = await apiClient.post<{ featureRequest: FeatureRequest }>('/v1/feature-requests', data);
  return response.data.featureRequest;
}

export async function fetchFeatureRequests(params?: FeatureRequestListParams): Promise<FeatureRequestListResponse> {
  const response = await apiClient.get<FeatureRequestListResponse>('/v1/feature-requests', { params });
  return response.data;
}

export async function fetchFeatureRequest(id: string): Promise<FeatureRequest> {
  const response = await apiClient.get<{ featureRequest: FeatureRequest }>(`/v1/feature-requests/${id}`);
  return response.data.featureRequest;
}

export async function fetchFeatureRequestStats(): Promise<FeatureRequestStats> {
  const response = await apiClient.get<{ stats: FeatureRequestStats }>('/v1/feature-requests/stats');
  return response.data.stats;
}

export async function fetchMyFeatureRequests(): Promise<{ featureRequests: FeatureRequest[] }> {
  const response = await apiClient.get<{ featureRequests: FeatureRequest[] }>('/v1/feature-requests/my-submissions');
  return response.data;
}

export async function updateFeatureRequest(id: string, data: UpdateFeatureRequestData): Promise<FeatureRequest> {
  const response = await apiClient.patch<{ featureRequest: FeatureRequest }>(`/v1/feature-requests/${id}`, data);
  return response.data.featureRequest;
}

export async function deleteFeatureRequest(id: string): Promise<void> {
  await apiClient.delete(`/v1/feature-requests/${id}`);
}

export async function voteForFeature(id: string): Promise<{ voteCount: number }> {
  const response = await apiClient.post<{ message: string; voteCount: number }>(`/v1/feature-requests/${id}/vote`);
  return { voteCount: response.data.voteCount };
}

export async function removeFeatureVote(id: string): Promise<{ voteCount: number }> {
  const response = await apiClient.delete<{ message: string; voteCount: number }>(`/v1/feature-requests/${id}/vote`);
  return { voteCount: response.data.voteCount };
}
